import { useCallback, useEffect, useState } from "react";
import { fetchScreenData } from "../../services/api";
import { runtimeConfig } from "../../config/runtime";
import { NON_IMAGE_TYPES } from "../../utils/assetPrefetcher";
import useLabbaikItems, { LabbaikItem } from "../../hooks/useLabbaikItems";
import { ScreenData } from "../../types/ScreenTypes";

// An image poster, or a Labbaik item shown as the masjid screens' Labbaik card.
export interface HomePoster {
  id: string;
  title: string;
  url?: string;
  labbaik?: LabbaikItem;
}

export type PosterColours = Pick<
  ScreenData,
  "primaryColor" | "secondaryColor" | "widgetBackgroundColor"
>;

const SEEN_KEY = "m247-home-posters-seen";
const SEEN_KEEP = 500;
const REFRESH_MS = 60 * 60 * 1000;

// The API sends UTC without a "Z", which Date.parse would read as the tablet's local time.
const parseUtc = (value: string) => Date.parse(/Z|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);

// The poster images on the masjid's "home" screen: its public posters, the playlists it ticked for
// household tablets and janāzah notices. Du'as, videos and HTML only play on the screen itself.
// Dates are checked again because an offline copy can be days old.
export const posterItems = (screen: ScreenData): HomePoster[] => {
  const now = Date.now();
  const posters = new Map<string, HomePoster>();
  // The screen endpoint sends flat assetUrl/assetType on items, as in assetPrefetcher
  (screen.playlists || []).forEach((playlist) => {
    (playlist.items || []).forEach((item: any) => {
      const url = item.asset?.url ?? item.assetUrl;
      const type = (item.asset?.type ?? item.assetType ?? "").toLowerCase();
      const id = String(item.assetId ?? item.id);
      if (!url || url.startsWith("<") || !type || NON_IMAGE_TYPES.includes(type)) return;
      if (item.isActive === false || posters.has(id)) return;
      if (item.assetActiveFromUtc && parseUtc(item.assetActiveFromUtc) > now) return;
      if (item.assetExpiryDate && parseUtc(item.assetExpiryDate) <= now) return;
      posters.set(id, {
        id,
        title: item.assetTitle ?? item.asset?.title ?? "",
        url: runtimeConfig.getBlobUrl(url),
      });
    });
  });
  return [...posters.values()];
};

// Ids were numbers before Labbaik items joined, so older lists still count.
const loadSeen = (): string[] => {
  try {
    return (JSON.parse(localStorage.getItem(SEEN_KEY) || "[]") as unknown[]).map(String);
  } catch {
    return [];
  }
};

// The masjid's posters for the Posters button, and which ones this tablet has already shown:
// its own posters, then its Labbaik items, in the website's order. "Prayer times only" screens
// carry no posters, so this reads the "home" screen, which the offline copies keep for every
// favourite.
export const usePosters = (masjidId: number, portrait: boolean, enabled: boolean) => {
  const [own, setOwn] = useState<HomePoster[]>([]);
  const [showLabbaik, setShowLabbaik] = useState(false);
  const [colours, setColours] = useState<PosterColours>({});
  const [seen, setSeen] = useState<string[]>(loadSeen);
  const labbaik = useLabbaikItems(masjidId, enabled && showLabbaik);

  useEffect(() => {
    setOwn([]);
    setShowLabbaik(false);
    if (!enabled) return;
    let current = true;
    const load = () =>
      fetchScreenData(masjidId, portrait ? "home-portrait" : "home")
        .then((screen) => {
          if (!current) return;
          setOwn(posterItems(screen));
          setShowLabbaik(!!screen.showLabbaik);
          setColours({
            primaryColor: screen.primaryColor,
            secondaryColor: screen.secondaryColor,
            widgetBackgroundColor: screen.widgetBackgroundColor,
          });
        })
        .catch(() => {});
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [masjidId, portrait, enabled]);

  useEffect(() => {
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
    } catch {
      // Private mode or full storage: the dot just comes back after a restart.
    }
  }, [seen]);

  const markSeen = useCallback(
    (id: string) =>
      setSeen((prev) => (prev.includes(id) ? prev : [...prev, id].slice(-SEEN_KEEP))),
    [],
  );

  const posters: HomePoster[] = [
    ...own,
    ...labbaik.map((item) => ({
      id: `labbaik:${item.linkUrl || item.title}`,
      title: item.title || "",
      labbaik: item,
    })),
  ];

  return { posters, colours, seen, markSeen };
};
